import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { SvgIcon } from "@/components/icon"
import DividerWithHex from "@/components/modal-dark/components/divider-with-hex/DividerWithHex"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { dismissUploadGeometryNotice, markUploadGeometryNoticeSeenThisSession } from "@/helpers/uploadGeometryNotice"
import { Checkbox, Col, Row } from "antd"
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint"
import { FC, useState } from "react"
import "./UploadGeometryNotice.less"

const { Title, Paragraph } = WebsiteTypography

const UploadGeometryNotice: FC = () => {
  const { xxl, xl, lg, md } = useBreakpoint()
  const isMobileScr = !(xxl || xl || lg || md)

  const { user } = useAuth()
  const { setIsModalDarkOpen, onSubmit, setOnSubmit } = useModalDarkContext()
  const [isDontShowAgain, setIsDontShowAgain] = useState(false)

  // onSubmit opens the native file picker, so it must run synchronously inside this click handler
  const handleProceed = () => {
    markUploadGeometryNoticeSeenThisSession()
    if (isDontShowAgain) dismissUploadGeometryNotice(user?.id)

    onSubmit?.()
    setOnSubmit(undefined)
    setIsDontShowAgain(false)
    setIsModalDarkOpen(false)
  }

  // Close does not count as "seen" — the notice will show again on the next upload click
  const handleClose = () => {
    setOnSubmit(undefined)
    setIsDontShowAgain(false)
    setIsModalDarkOpen(false)
  }

  return (
    <Row justify={"center"} className={"upload-geometry-notice-wrapper"} style={{ paddingBottom: 24 }}>
      <Col span={18} xxl={18} xl={18} lg={20} md={24} sm={24} xs={24}>
        <Row justify={"center"} className={"upload-geometry-notice-icon-wrapper"}>
          <div className={"upload-geometry-notice-hex-wrapper"}>
            <SvgIcon type={"map-pinned-geometry"} />
          </div>
        </Row>

        <Title
          level={isMobileScr ? 5 : 3}
          centered
          className={"section-title"}
          style={{ paddingTop: 12, paddingBottom: 16 }}
        >
          <span className={"bold"}>{"Keep it simple: "}</span>
          <span className={"w300"}>{"route or area only"}</span>
        </Title>

        <Row justify={"center"} style={{ paddingTop: 4 }}>
          <Col span={24}>
            <Paragraph
              centered
              size={isMobileScr ? "sm" : "md"}
              color={"website-secondary"}
              weight={"w300"}
              style={{ paddingBottom: 24 }}
            >
              {
                "Upload a simple geometry showing just your project’s route or boundary — that’s all we need at this stage."
              }
            </Paragraph>
            <Paragraph size={isMobileScr ? "xs" : "sm"} color={"website-secondary"} weight={"w300"} centered>
              {
                "Please hold off on detailed CAD drawings here. You’ll get the chance to upload those as supporting documents in "
              }
              <span className={"bold"}>{"Step 3 (Upload Documents)"}</span>
              {"."}
            </Paragraph>
          </Col>
        </Row>

        <DividerWithHex />
      </Col>

      <Row justify={"center"} style={{ width: "100%", paddingBottom: 16 }}>
        <Checkbox
          className={"upload-geometry-notice-checkbox"}
          checked={isDontShowAgain}
          onChange={e => setIsDontShowAgain(e.target.checked)}
        >
          <Paragraph size={"xs"} color={"website-secondary"} weight={"w300"}>
            {"Don’t show this again"}
          </Paragraph>
        </Checkbox>
      </Row>

      <Col span={!(xxl || xl || lg || md) ? 24 : 20}>
        <Row justify={"center"} style={{ paddingTop: 8, width: "100%" }} gutter={[16, 16]}>
          <Col span={isMobileScr ? 24 : 12}>
            <Button btnType={"ghost"} text={"Close"} onClick={handleClose} block textColor={"white"} size={"large"} />
          </Col>

          <Col span={isMobileScr ? 24 : 12}>
            <Button btnType={"primary"} text={"Proceed"} onClick={handleProceed} block size={"large"} />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default UploadGeometryNotice
